import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, ChevronDown, ChevronRight, ExternalLink, Lightbulb, CalendarClock, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { useAppContext } from "@/lib/onboardingContext";
import { handleExternalClick } from "@/lib/externalLink";

function formatLabel(text: string): string {
  if (text.includes(" ")) return text;
  return text.replace(/-/g, " ").replace(/^./, c => c.toUpperCase());
}

const AVG_DAYS_PER_TASK = 3;

export default function Checklist() {
  const navigate = useNavigate();
  const { results: rawResults, ensureResults } = useAppContext();
  const results = rawResults ?? ensureResults();
  const [checked, setChecked] = useState<Set<string>>(new Set(results?.checklist.filter(c => c.done).map(c => c.id) || []));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const milestones = useMemo(() => [
    { pct: 25, label: "Foundation" },
    { pct: 50, label: "Halfway" },
    { pct: 75, label: "Almost There" },
    { pct: 100, label: "Ready to Open!" },
  ], []);

  // results is guaranteed non-null via ensureResults() above.

  const toggleCheck = (id: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const total = results.checklist.length;
  const completed = checked.size;
  const progress = Math.round((completed / total) * 100);
  const remaining = total - completed;
  const estDays = remaining * AVG_DAYS_PER_TASK;

  const currentMilestone = milestones.filter(m => progress >= m.pct).pop();

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-medium text-foreground">Opening Plan</h1>
            <p className="text-sm text-muted-foreground">{completed} of {total} steps complete</p>
          </div>
        </div>
      </div>

      <div className="container pt-6 max-w-lg space-y-6">
        {/* Progress Hero Card */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardContent className="pt-5 pb-4 px-5 space-y-4">
              {/* Percentage & Milestone */}
              <div className="flex items-center justify-between">
                <div>
                  <span className={`text-3xl font-medium ${
                    progress >= 100 ? "text-primary" : progress >= 50 ? "text-foreground" : "text-foreground"
                  }`}>{progress}%</span>
                  <span className="text-sm text-muted-foreground ml-2">complete</span>
                </div>
                {currentMilestone && (
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    progress >= 100 ? "bg-primary/10 text-primary" :
                    progress >= 75 ? "bg-primary/10 text-primary" :
                    progress >= 50 ? "bg-accent/10 text-accent-foreground" :
                    "bg-muted text-muted-foreground"
                  }`}>
                    <Flag className="w-3 h-3 inline mr-1" />
                    {currentMilestone.label}
                  </span>
                )}
              </div>

              {/* Progress Bar with milestones */}
              <div className="relative">
                <Progress value={progress} className="h-3" />
                <div className="flex justify-between mt-1.5">
                  {milestones.map(m => (
                    <div key={m.pct} className="flex flex-col items-center" style={{ width: 0, marginLeft: `${m.pct}%`, transform: "translateX(-50%)" }}>
                      <div className={`w-1.5 h-1.5 rounded-full ${progress >= m.pct ? "bg-primary" : "bg-muted-foreground/30"}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats Row */}
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-lg font-medium text-foreground">{completed}</p>
                    <p className="text-xs text-muted-foreground">Done</p>
                  </div>
                  <div className="w-px h-8 bg-border" />
                  <div className="text-center">
                    <p className="text-lg font-medium text-foreground">{remaining}</p>
                    <p className="text-xs text-muted-foreground">Remaining</p>
                  </div>
                </div>
                {remaining > 0 && (
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CalendarClock className="w-4 h-4" />
                    <span className="text-sm font-medium">~{estDays} days to opening</span>
                  </div>
                )}
                {remaining === 0 && (
                  <span className="text-sm font-medium text-primary">🎉 Ready to open!</span>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Checklist Items */}
        <div className="space-y-2">
          {results.checklist.map((item, i) => {
            const isDone = checked.has(item.id);
            const isOpen = expanded.has(item.id);
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className={`rounded-xl border transition-all ${
                  isDone ? "border-primary/30 bg-primary/5" : "border-border bg-card"
                }`}
              >
                <div className="flex items-center gap-3 p-4">
                  <button
                    onClick={() => toggleCheck(item.id)}
                    className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 transition-all ${
                      isDone ? "bg-primary" : "border-2 border-border hover:border-primary/50"
                    }`}
                  >
                    {isDone && <Check className="w-4 h-4 text-primary-foreground" />}
                  </button>
                  <span className={`text-sm flex-1 ${isDone ? "line-through text-muted-foreground" : "text-foreground"}`}>
                    {formatLabel(item.text)}
                  </span>
                  <button onClick={() => toggleExpand(item.id)} className="text-muted-foreground hover:text-foreground">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                </div>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 border-t border-border pt-3 ml-9">
                        <div className="flex items-start gap-2 mb-3 bg-primary/5 rounded-lg p-2.5">
                          <Lightbulb className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          <p className="text-xs text-muted-foreground">{item.tip}</p>
                        </div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Where to get it:</p>
                        <div className="space-y-1.5">
                          {item.sources.map((src, j) => (
                            <a
                              key={j}
                              href={src.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={handleExternalClick}
                              className="flex items-center gap-2 text-sm text-primary hover:underline"
                            >
                              <ExternalLink className="w-3 h-3" />
                              {src.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}